import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Spinner,
  Table,
  TableHead,
  TableHeadCell,
  TableRow,
  TableCell,
  TableBody,
  TextInput,
  Select,
} from "flowbite-react";

import AuctionFormModal from "../components/auctions/AuctionFormModal";
import {
  auctionsActions,
  auctionsThunks,
  countiesThunks,
  statesThunks,
  usersThunks,
  auctionRuleTemplatesThunks,
} from "../app/store";
import { LuList } from "react-icons/lu";
import { FiEdit2 } from "react-icons/fi";

const AUCTION_TYPES = ["AUCTION", "OTC", "LAND_BANK", "COUNTY_HELD"];
const RESEARCH_STATUS_LABELS = {
  PENDING: "Pendente",
  READY: "Pronto p/ VA",
  IN_PROGRESS: "Em andamento",
  WAITING_ADMIN: "Aguardando Admin",
  APPROVED: "Aprovado",
  ABANDONED: "Abandonado",
};
const PAYMENT_STATUS_LABELS = {
  UNPAID: "Não pago",
  PAID: "Pago",
};

export default function AuctionsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { items, count, status, error } = useSelector((s) => s.auctions);
  const countiesById = useSelector((s) => s.counties.byId);
  const authUser = useSelector((s) => s.auth?.user || null);
  const users = useSelector((s) => s.users?.items);
  const statesById = useSelector((s) => s.states.byId);
  const ruleTemplatesById = useSelector((s) => s.auctionRuleTemplates.byId);

  const [q, setQ] = useState("");
  const [stateId, setStateId] = useState("");
  const [countyId, setCountyId] = useState("");
  const [ruleTemplateId, setRuleTemplateId] = useState("");
  const [auctionType, setAuctionType] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [researchStatus, setResearchStatus] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [startsAtOrder, setStartsAtOrder] = useState("DESC"); // DESC | ASC
  const [hidePast, setHidePast] = useState(true); // true = esconde leilões passados
  const [hideAbandoned, setHideAbandoned] = useState(true);

  const usersById = useSelector((s) => s.users.byId);

  const isAdmin = authUser?.role === "ADMIN";
  const isVA = authUser?.role === "VA";
  const currentUserId = authUser?.id || null;

  const limit = useMemo(() => 20, []);
  const offset = 0;

  // const canEdit = isAdmin || (isVA && r.assigned_to_user_id === currentUserId);
  // const canOpen = isAdmin || (isVA && r.assigned_to_user_id === currentUserId);

  useEffect(() => {
    dispatch(statesThunks.list({ q: "", limit: 9999, offset: 0 }));
    dispatch(countiesThunks.list({ q: "", limit: 9999, offset: 0 }));
    dispatch(
      auctionRuleTemplatesThunks.list({ q: "", limit: 9999, offset: 0 }),
    );
    dispatch(usersThunks.list({ q: "", limit: 9999, offset: 0 }));
    dispatch(auctionsThunks.list({ q: "", limit, offset }));
  }, [dispatch, limit]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
    dispatch(auctionsActions.clearError());
  }

  function buildListParams() {
    return {
      q: q || "",
      state_id: stateId || undefined,
      county_id: countyId || undefined,
      rule_template_id: ruleTemplateId || undefined,
      auction_type: auctionType || undefined,
      research_status: researchStatus || undefined,

      // ✅ VA: força assigned_to_user_id = ele mesmo
      assigned_to_user_id: isVA ? currentUserId : assignedTo || undefined,

      payment_status: paymentStatus || undefined,
      limit,
      offset,
    };
  }

  const listParams = useMemo(
    () => buildListParams(),
    [
      q,
      stateId,
      countyId,
      ruleTemplateId,
      auctionType,
      researchStatus,
      assignedTo,
      paymentStatus,
      limit,
      offset,
    ],
  );

  useEffect(() => {
    dispatch(auctionsActions.clearError());
    dispatch(auctionsThunks.list(listParams));
  }, [dispatch, listParams]);

  function onSearch() {
    dispatch(auctionsActions.clearError());
    dispatch(auctionsThunks.list(buildListParams()));
  }

  async function onSubmit(payload) {
    const currentUserId = authUser?.id || null;

    if (editing?.id) {
      const mergedPayload = {
        ...editing,
        ...payload,
        sampled_by_admin_id: editing.sampled_by_admin_id || currentUserId,
      };

      console.log("PAYLOAD RECEBIDO NO PAGE:", payload);
      console.log("EDITING:", editing);

      await dispatch(
        auctionsThunks.updateOne({
          id: editing.id,
          payload: mergedPayload,
        }),
      );

      dispatch(auctionsThunks.list(buildListParams()));
      closeModal();
      return;
    }

    await dispatch(
      auctionsThunks.createOne({
        ...payload,
        sampled_by_admin_id: currentUserId,
      }),
    );

    dispatch(auctionsThunks.list(buildListParams()));
    closeModal();
  }
  const isLoading = status === "loading";

  const stateOptions = useMemo(() => {
    const arr = Object.values(statesById || {});
    return arr.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
    );
  }, [statesById]);

  const countyOptions = useMemo(() => {
    let arr = Object.values(countiesById || {});

    if (stateId) {
      arr = arr.filter((c) => c.state_id === stateId);
    }

    return arr.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
    );
  }, [countiesById, stateId]);

  const templateOptions = useMemo(() => {
    const arr = Object.values(ruleTemplatesById || {});
    return arr.sort((a, b) =>
      String(a?.title || "").localeCompare(String(b?.title || "")),
    );
  }, [ruleTemplatesById]);

  const visibleItems = useMemo(() => {
    const now = new Date();
    let arr = Array.isArray(items) ? [...items] : [];

    if (hideAbandoned) {
      arr = arr.filter((a) => a?.research_status !== "ABANDONED");
    }
    // ✅ VA só vê o que é dele
    if (isVA && currentUserId) {
      arr = arr.filter((a) => a?.assigned_to_user_id === currentUserId);
    }
    

    if (hidePast) {
      arr = arr.filter((a) => {
        if (!a?.starts_at) return true;
        return new Date(a.starts_at) >= now;
      });
    }


    arr.sort((a, b) => {
      const da = a?.starts_at ? new Date(a.starts_at).getTime() : 0;
      const db = b?.starts_at ? new Date(b.starts_at).getTime() : 0;
      return startsAtOrder === "ASC" ? da - db : db - da;
    });

    return arr;
  }, [items, hidePast, hideAbandoned, startsAtOrder, isVA, currentUserId]);

  const visibleCount = useMemo(() => {
    if (isVA) return visibleItems.length;
    return count;
  }, [isVA, visibleItems.length, count]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Auctions
          </h1>
          <p className="text-sm text-gray-500">{visibleCount} registro(s)</p>
        </div>

        {isAdmin && (
          <Button className="text-slate-600" onClick={openCreate}>
            Novo Auction
          </Button>
        )}
      </div>

      {error && (
        <Alert
          color="failure"
          onDismiss={() => dispatch(auctionsActions.clearError())}
        >
          {error}
        </Alert>
      )}

      <div className="flex flex-col md:flex-row md:items-center gap-2">
        {/* <TextInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar (opcional)..."
        /> */}

        <Select
          value={stateId}
          onChange={(e) => {
            setStateId(e.target.value);
            setCountyId("");
          }}
        >
          <option value="">Todos os Estados</option>
          {stateOptions.map((st) => (
            <option key={st.id} value={st.id}>
              {st.name}
            </option>
          ))}
        </Select>

        <Select
          value={countyId}
          disabled={!stateId}
          onChange={(e) => setCountyId(e.target.value)}
        >
          <option value="">
            {stateId ? "Todos os Counties" : "Selecione um Estado"}
          </option>
          {countyOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>

        {isAdmin && (
          <Select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
          >
            <option value="">Todos os VAs</option>
            {Object.values(usersById || {}).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </Select>
        )}

        <Select
          value={paymentStatus}
          onChange={(e) => setPaymentStatus(e.target.value)}
        >
          <option value="">Pagamento</option>
          {Object.entries(PAYMENT_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>

        <Select
          value={auctionType}
          onChange={(e) => setAuctionType(e.target.value)}
        >
          <option value="">Todos os Types</option>
          {AUCTION_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select
          value={startsAtOrder}
          onChange={(e) => setStartsAtOrder(e.target.value)}
        >
          <option value="DESC">Starts At ↓ (mais recente)</option>
          <option value="ASC">Starts At ↑ (mais antigo)</option>
        </Select>

        <Select
          value={hidePast ? "HIDE" : "SHOW"}
          onChange={(e) => setHidePast(e.target.value === "HIDE")}
        >
          <option value="HIDE">Ocultar passadas</option>
          <option value="SHOW">Mostrar passadas</option>
        </Select>

        <Select
          value={hideAbandoned ? "HIDE" : "SHOW"}
          onChange={(e) => setHideAbandoned(e.target.value === "HIDE")}
        >
          <option value="HIDE">Ocultar abandonados</option>
          <option value="SHOW">Mostrar abandonados</option>
        </Select>

        {/* <Button color="gray" onClick={onSearch} disabled={isLoading}>
          Buscar
        </Button> */}
      </div>

      <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-6 flex items-center gap-3">
            <Spinner />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Carregando...
            </span>
          </div>
        ) : (
          <Table hoverable>
            <TableHead>
              <TableHeadCell>Ações</TableHeadCell>
              <TableHeadCell>State</TableHeadCell>
              <TableHeadCell>County</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell>Responsável</TableHeadCell>
              <TableHeadCell>Type</TableHeadCell>
              <TableHeadCell>Deadline</TableHeadCell>
              <TableHeadCell>Starts At</TableHeadCell>
              <TableHeadCell>Pagamento</TableHeadCell>
            </TableHead>

            <TableBody className="divide-y">
              {items?.length ? (
                visibleItems.map((r) => (
                  <TableRow
                    key={r.id}
                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {isAdmin && (
                          <button
                            onClick={() => openEdit(r)}
                            title="Editar Auction"
                            className="text-gray-500 hover:text-blue-600 transition"
                          >
                            <FiEdit2 size={16} />
                          </button>
                        )}

                        <button
                          onClick={() =>
                            navigate(`/app/auctions/${r.id}/properties`)
                          }
                          title="Ver Propriedades"
                          className="text-gray-500 hover:text-green-600 transition"
                        >
                          <LuList size={18} />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {statesById?.[countiesById?.[r.county_id]?.state_id]
                        ?.code || "—"}
                    </TableCell>
                    <TableCell className="font-medium text-gray-900 dark:text-white">
                      {countiesById?.[r.county_id]?.name || "—"}
                    </TableCell>

                    <TableCell>
                      {RESEARCH_STATUS_LABELS[r.research_status] ||
                        r.research_status}
                    </TableCell>

                    <TableCell>
                      {r.assigned_to_user_id
                        ? usersById?.[r.assigned_to_user_id]?.name ||
                          usersById?.[r.assigned_to_user_id]?.email ||
                          "—"
                        : "—"}
                    </TableCell>

                    <TableCell>{r.auction_type}</TableCell>

                    <TableCell>
                      {r.research_due_at
                        ? new Date(r.research_due_at).toLocaleString()
                        : "—"}
                    </TableCell>

                    <TableCell>
                      {r.starts_at
                        ? new Date(r.starts_at).toLocaleString()
                        : "—"}
                    </TableCell>

                    <TableCell>
                      {PAYMENT_STATUS_LABELS[r.payment_status] ||
                        r.payment_status}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-6 text-gray-500"
                  >
                    Nenhum auction encontrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <AuctionFormModal
        open={modalOpen}
        onClose={closeModal}
        initialData={editing}
        onSubmit={onSubmit}
        saving={isLoading}
        states={stateOptions}
        counties={Object.values(countiesById || {})} // todos, sem filtro
        ruleTemplates={templateOptions}
        users={users}
        currentUserId={authUser?.id || null}
      />
    </div>
  );
}
